<template>
  <div
    ref="overview"
    class="quick-view-overview is-forward-animation"
    v-data-test-id="'quick-view-overview'"
  >
    <TopGalleryMobile
      class="quick-view-overview__top-gallery"
      :currentVariant="currentVariant"
      :galleryMargin="40"
      :galleryMaxWidth="400"
      :isLazyLoaded="false"
      :isLightboxEnabled="false"
      :isVideoControlVisible="false"
      :isGreyedOutEnabled="true"
      :paginationColor="'#999'"
      :product="product"
      :slideIndex="0"
      :slideResetter="slideResetter"
    />
    <VariantSlider
      class="quick-view-overview__variant-slider"
      :variants="variants"
      :currentVariantId="currentVariant.spreeProduct.id"
      :slidesPerPage="5"
      :spacePadding="30"
      @slide-click="updateVariant"
    />
    <div class="quick-view-overview__description-box">
      <div class="quick-view-overview__title-box">
        <div class="quick-view-overview__product-title">{{ product.name }}</div>
        <div class="quick-view-overview__price">
          {{ priceWithCurrency }}
        </div>
      </div>
      <div class="quick-view-overview__color">
        {{ currentVariant.color }}
      </div>
      <div class="quick-view-overview__color">
        {{ $t(`pdp.purchase_pod.${currentVariant.gender}`) }}
      </div>
    </div>

    <div class="quick-view-overview__button-box">
      <Button
        class="quick-view-overview__button"
        :class="{ 'quick-view-overview__button--invisible': isOngoingRaffle }"
        :height="BUTTON_STYLES.height.small"
        :isDisabled="isRaffle || (isMembersOnlyProduct && !this.isLoggedIn)"
        :fontSize="BUTTON_STYLES.fontSize.small"
        @click="triggerStateChange"
        v-data-test-id="'quick-add-button'"
        >{{ addToCartButtonText }}</Button
      >
      <Button
        class="quick-view-overview__button"
        :href="currentVariant.spreeProduct.productUrl"
        :color="buttonColor"
        :fontSize="BUTTON_STYLES.fontSize.small"
        :height="BUTTON_STYLES.height.small"
        tag="a"
        @click="goToProductPage"
        >{{ pdpLinkButtonText }}</Button
      >
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { formatToCurrency } from '@/helpers/currency-formatter'
import { getPathFromUrl } from '@/helpers/url.js'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import TopGalleryMobile from '@/components/ProductPage/TopGallery/TopGalleryMobile.vue'
import VariantSlider from '@/components/_shared/VariantSliders/VariantSlider.vue'
import { STATES } from '@/components/_shared/Cards/Card/QuickView/QuickView.vue'

export default {
  name: 'QuickViewOverview',
  components: { Button, TopGalleryMobile, VariantSlider },
  props: {
    product: {
      type: Object,
      required: true,
    },
    sku: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      BUTTON_STYLES,
      slideResetter: [],
    }
  },

  computed: {
    ...mapState('store', ['currency', 'sizeSystem']),
    ...mapState('page', ['locale']),
    ...mapGetters({ isLoggedIn: 'account/loggedIn' }),

    addToCartButtonText() {
      return this.hasRaffleEnded
        ? this.$t('filter.quickview.draw_closed')
        : `+  ${this.$t('filter.quickview.add')}`
    },

    pdpLinkButtonText() {
      return this.isOngoingRaffle
        ? this.$t('filter.quickview.enter_draw')
        : this.$t('filter.quickview.learn_more')
    },

    isRaffle() {
      return this.currentVariant.spreeProduct.isRaffle
    },

    hasRaffleEnded() {
      return this.currentVariant.spreeProduct.hasRaffleEnded
    },

    isOngoingRaffle() {
      return this.isRaffle && !this.hasRaffleEnded
    },

    buttonColor() {
      return this.isOngoingRaffle ? this.BUTTON_STYLES.color.dark : this.BUTTON_STYLES.color.white
    },

    variants() {
      return this.product.variants.reduce((thumbs, variant) => {
        thumbs.push({
          imageUrl: variant.defaultImageSmall.mediaUrl,
          sku: Number(variant.spreeProduct.sku),
          id: Number(variant.spreeProduct.id),
        })
        return thumbs
      }, [])
    },

    isMembersOnlyProduct() {
      return this.currentVariant.isMembersOnly
    },

    currentVariant() {
      return (
        this.product.variants.find(v => Number(v.spreeProduct.sku) === this.sku) ||
        this.product.variants[0]
      )
    },

    currentVariantIndex() {
      return this.variants.findIndex(variant => variant.sku === this.sku)
    },

    priceWithCurrency() {
      const amount = Number(this.currentVariant?.spreeProduct.price)
      return formatToCurrency(amount, this.locale, this.currency)
    },
  },

  watch: {
    currentVariant: 'resetSlide',
    currentVariantIndex: 'trackProductListView',
  },

  methods: {
    triggerStateChange() {
      this.$emit('state-change', STATES.sizes)
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'quickview_1', label: 'add_to_cart' },
      })
    },

    resetSlide() {
      this.slideResetter = [0, false]
    },

    updateVariant({ currentVariantSku }) {
      this.$emit('update-sku', currentVariantSku)
    },

    goToProductPage() {
      this.trackProductPageVisit()
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'quickview_1', label: 'learn_more' },
      })
    },

    /* Variant slider - event tracking */
    trackProductListView() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listView,
        productData: {
          viewmode: 'quickview',
          items: [
            {
              id: this.currentVariant?.spreeProduct?.sku,
              price: this.currentVariant?.spreeProduct.price,
              list: this.context || getPathFromUrl(window.location.href),
              position: this.currentVariantIndex + 1,
            },
          ],
        },
      })
    },

    /* Learn more button - event tracking */
    trackProductPageVisit() {
      const productData = {
        viewmode: 'quickview',
        items: {
          id: this.currentVariant?.spreeProduct?.sku,
          price: this.currentVariant?.spreeProduct.price,
          position: this.currentVariantIndex + 1,
        },
        currency: this.currency.code,
        list: this.context || getPathFromUrl(window.location.href),
        location: this.currentVariant.spreeProduct.productUrl,
      }

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listClick,
        productData,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.quick-view-overview {
  transform: translateX(0);

  &__top-gallery {
    border-radius: 8px; // Needed for some iOS devices
  }

  &__variant-slider {
    min-height: 65px;
  }

  &__description-box {
    padding: 0 20px 10px;
  }

  &__title-box {
    display: flex;
  }

  &__product-title {
    flex: 1;
    font-weight: bold;
    text-align: left;
    color: $color-black;
  }

  &__price {
    font-size: $f-size-xsmall;
    color: $c-grey-dark-2;
    padding-top: 4px;
  }

  &__color {
    font-size: $f-size-xsmall;
    color: $c-grey-mid-1;
    text-align: left;
  }

  &__button-box {
    padding: 0 20px 20px;
  }

  &__button {
    margin-top: 12px;
    transition: opacity $transition-time;

    &--invisible {
      opacity: 0;
    }
  }

  /deep/ .top-gallery-item {
    padding: 0;

    &--no-touch {
      padding: 40px;
    }
  }
}
</style>
